import React, { Component } from 'react';
import { get, flowRight } from 'lodash';
import { type WithTranslation } from '@wix/yoshi-flow-editor';
import {
  SECTION_CATEGORY,
  isLayoutProGallery,
  createPageUrl,
} from '@wix/communities-blog-client-common';
import stylesParams from '../../../../components/Blog/stylesParams';
import AnimatedLoader from '../../../common/components/animated-loader';
import withComponents, {
  type WithComponents,
} from '../../../common/components/components-provider/with-components';
import { EmptyLayout } from '../../../common/components/layout';
import Page from '../../../common/components/page';
import { connect } from '../../../common/components/runtime-context';
import Pagination from '../../../common/containers/pagination';
import { PostTitleTagProvider } from '../../../common/context/post-title-tag-context';
import withAuth, { type WithAuth } from '../../../common/hoc/with-auth';
import withLayoutProps, { type WithLayoutProps } from '../../../common/hoc/with-layout-props';
import withPermissions, { type WithPermissions } from '../../../common/hoc/with-permissions';
import withTranslate from '../../../common/hoc/with-translate';
import { getCanSeeMoreButton } from '../../../common/selectors/app-settings-selectors';
import { isCreatePostButtonVisible } from '../../../common/selectors/blog-menu-button-selectors';
import {
  isExcludePostContentSupported,
  isPostContentRequired,
} from '../../../common/selectors/layout-selectors';
import {
  getSortedPostsByArchiveDate,
  getPostsByArchiveDateAndPage,
} from '../../../common/selectors/post-selectors';
import { getArchiveUrl } from '../../../common/services/get-archive-url';
import { handleNavigation } from '../../../common/services/navigation';
import {
  getIsMobile,
  getTimezone,
  isSite,
} from '../../../common/store/basic-params/basic-params-selectors';
import { getIsArchiveLoaded } from '../../../common/store/is-loaded/is-loaded-selectors';
import { getIsArchiveLoading } from '../../../common/store/is-loading/is-loading-selectors';
import { getSectionUrl } from '../../../common/store/topology/topology-selectors';
import PostList from '../../components/post-list';

import withPaginationSettings, {
  type WithPaginationSettings,
} from '../../hoc/with-pagination-settings';

const Layout = EmptyLayout;

type Props = OwnProps &
  WithComponents &
  WithPermissions &
  WithPaginationSettings &
  WithLayoutProps &
  WithAuth &
  Pick<WithTranslation, 't'> &
  ReturnType<typeof mapRuntimeToProps>;

class ArchivePage extends Component<Props> {
  componentWillUnmount() {
    this.props.setIsLoaded('archive', null, true);
  }

  componentDidUpdate({ isAuthenticated, isBlocked, pageSize, excludeContent }: Props) {
    const { year, month } = this.props;
    if (
      this.props.isAuthenticated !== isAuthenticated ||
      this.props.isBlocked !== isBlocked ||
      this.props.pageSize !== pageSize ||
      (excludeContent && !this.props.excludeContent)
    ) {
      this.fetchPosts(year, month);
    }
  }

  fetchPosts(year: string, month: string, page = this.props.page) {
    if (this.props.isBlocked) {
      return;
    }

    return this.props.fetchArchive(year, month, page);
  }

  loadMore = (page: number) => this.fetchPosts(this.props.year, this.props.month, page);

  renderPosts() {
    const {
      currentPagePosts,
      allPosts,
      entityCount,
      showCreatePostAction,
      page,
      layoutType,
      showPagination,
      isArchiveLoading,
      isArchiveLoaded,
      PostListProGallery,
      canSeeMoreButton,
    } = this.props;

    if (layoutType && isLayoutProGallery(layoutType)) {
      return (
        <PostListProGallery
          pageStart={page + 1}
          entityCount={entityCount}
          loadMore={showPagination ? undefined : this.loadMore}
          allPosts={showPagination ? currentPagePosts : allPosts}
          currentPagePosts={currentPagePosts}
          isLoading={isArchiveLoading}
          showCreatePostAction={showCreatePostAction}
          canSeeMoreButton={canSeeMoreButton}
          section={SECTION_CATEGORY}
        />
      );
    }

    return (
      <PostList
        pageStart={page + 1}
        layoutType={layoutType}
        entityCount={entityCount}
        loadMore={showPagination ? undefined : this.loadMore}
        allPosts={showPagination ? currentPagePosts : allPosts}
        currentPagePosts={currentPagePosts}
        isLoading={isArchiveLoading}
        showCreatePostAction={showCreatePostAction}
      />
    );
  }

  renderPagination() {
    const { entityCount, pageSize, page, archiveUrl, isSite, showPagination } = this.props;

    return showPagination ? (
      <Pagination
        dataHook="archive-pagination-container"
        page={page}
        pageSize={pageSize}
        entityCount={entityCount}
        createPageUrl={(page) => createPageUrl(page, archiveUrl)}
        handleNavigation={handleNavigation('archive', isSite)}
      />
    ) : null;
  }

  render() {
    const { allPosts, isArchiveLoading, isArchiveLoaded } = this.props;

    const isLoading = isArchiveLoading && !isArchiveLoaded;

    return (
      <PostTitleTagProvider titleFontParam={stylesParams.cssTitleFontCategory} fallbackTagName="h2">
        <Page noSpacing>
          <Layout>
            <AnimatedLoader isLoading={isLoading && !allPosts.length}>
              {this.renderPosts()}
            </AnimatedLoader>
            {!isLoading ? this.renderPagination() : null}
          </Layout>
        </Page>
      </PostTitleTagProvider>
    );
  }
}

type OwnProps = {
  params: { year: string; month: string };
  page: number;
  canSee: (action: string, entity: string) => boolean;
};

import { type Actions, type AppState } from '../../../common/types';

const mapRuntimeToProps = (state: AppState, ownProps: OwnProps, actions: Actions) => {
  const year = get(ownProps, ['params', 'year']);
  const month = get(ownProps, ['params', 'month']);

  const sectionUrl = getSectionUrl(state);

  const showCreatePostAction =
    getIsMobile(state) && isCreatePostButtonVisible(state, ownProps.canSee);

  return {
    year,
    month,
    archiveUrl: getArchiveUrl(sectionUrl, year, month),
    allPosts: year && month ? getSortedPostsByArchiveDate(state, { year, month }) : [],
    currentPagePosts:
      year && month
        ? getPostsByArchiveDateAndPage(
            state,
            // This selector composes two inputs that disagree on the second
            // argument (a date object vs an entity id), so reselect intersects
            // them. Pre-existing; the date is what this call site supplies.
            { year, month } as { year: string; month: string } & string,
            ownProps.page,
          )
        : [],
    isArchiveLoading: getIsArchiveLoading(state),
    isArchiveLoaded: getIsArchiveLoaded(state),
    isSite: isSite(state),
    showCreatePostAction,
    excludeContent: isExcludePostContentSupported(state),
    setIsLoaded: actions.setIsLoaded,
    fetchArchive: (year: string, month: string, page: number) =>
      actions.fetchArchivePostsWithAdapterPromisified({
        page,
        includeContent: isPostContentRequired(state),
        archiveOptions: {
          year: parseInt(year, 10),
          month: parseInt(month, 10),
          timeZone: getTimezone(state),
        },
      }),
    canSeeMoreButton: getCanSeeMoreButton(state, ownProps.canSee),
  };
};

export default flowRight(
  withPermissions,
  withComponents,
  withPaginationSettings(SECTION_CATEGORY),
  withLayoutProps(),
  withAuth,
  withTranslate,
  connect(mapRuntimeToProps),
)(ArchivePage);
